import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, AlertCircle, ArrowLeft, CheckCircle, Loader } from 'lucide-react';
import api from '../utils/api';
import useScreenSize from '../hooks/useScreenSize';
import { useSelector } from 'react-redux';
import { selectIsAuthenticated, selectUserType } from '../store/slices/authSlice';

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile } = useScreenSize();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const userType = useSelector(selectUserType);

  const [email, setEmail] = useState('');
  const [error, setError] = useState(location.state?.error || null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [submitCount, setSubmitCount] = useState(0);

  // Redirect if already authenticated
  React.useEffect(() => {
    if (isAuthenticated && userType) {
      navigate(`/${userType.toLowerCase()}/dashboard`);
    }
  }, [isAuthenticated, userType, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Basic email validation
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    // Rate limiting on client side
    if (submitCount >= 3) {
      setError('Too many attempts. Please try again later.');
      return;
    }

    setIsLoading(true);

    try {
      await api.post('/api/auth/forgot-password', { email });
      setIsSuccess(true);
      setSubmitCount(prev => prev + 1);
    } catch (err) {
      // Don't expose whether the email exists or not
      setIsSuccess(true); // Still show success even on error
      console.error('Password reset error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-indigo-900 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Animated background patterns */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,_rgba(99,102,241,0.15),transparent_70%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_60%,_rgba(192,132,252,0.15),transparent_70%)]" />
        <div className="absolute inset-0 opacity-30">
          <div className="absolute inset-0 bg-grid-white/10" />
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className={`
          relative z-10 max-w-md w-full backdrop-blur-xl bg-white/10 
          rounded-2xl shadow-2xl border border-white/20
          ${isMobile ? 'p-6' : 'p-8'}
        `}
      >
        {isSuccess ? (
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="text-center"
          >
            <div className="relative">
              <div className="absolute inset-0 animate-ping-slow">
                <CheckCircle className="w-12 h-12 text-green-500/50 mx-auto" />
              </div>
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4 relative z-10" />
            </div>
            <h2 className="text-xl font-bold text-white mb-4">
              Check Your Email
            </h2>
            <p className="text-gray-300 mb-6">
              If an account exists with {email}, we've sent instructions to reset your password.
            </p>
            <div className="space-y-4">
              <p className="text-sm text-gray-400">
                Don't see the email? Check your spam folder.
              </p>
              <div className="space-x-4">
                <button
                  onClick={() => {
                    setIsSuccess(false);
                    setEmail('');
                  }}
                  className="bg-white/10 text-white px-4 py-2 rounded-lg hover:bg-white/20 transition-colors"
                >
                  Try Again
                </button>
                <Link
                  to="/login"
                  className="bg-indigo-500 text-white px-4 py-2 rounded-lg hover:bg-indigo-600 transition-colors inline-block"
                >
                  Return to Login
                </Link>
              </div>
            </div>
          </motion.div>
        ) : (
          <>
            <div className="text-center mb-8">
              <div className="relative">
                <div className="absolute inset-0 animate-pulse">
                  <Mail className="w-12 h-12 text-indigo-500/50 mx-auto" />
                </div>
                <Mail className="w-12 h-12 text-indigo-500 mx-auto mb-4 relative z-10" />
              </div>
              <h2 className="text-xl font-bold text-white mb-2">
                Forgot Password?
              </h2>
              <p className="text-gray-300">
                Enter your email and we'll send you instructions to reset your password
              </p>
            </div>

            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-3"
              >
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                <p className="text-sm text-red-400">{error}</p>
              </motion.div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
              >
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-2 bg-white/10 border border-white/20 text-white rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 placeholder-gray-400"
                  placeholder="Enter your email"
                  disabled={isLoading}
                />
              </motion.div>

              <motion.button
                type="submit"
                disabled={isLoading || submitCount >= 3}
                className="w-full bg-gradient-to-r from-indigo-500 to-purple-500 text-white py-2 px-4 rounded-lg hover:from-indigo-600 hover:to-purple-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {isLoading ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin" />
                    Sending Instructions...
                  </>
                ) : (
                  'Send Reset Instructions'
                )}
              </motion.button>

              <div className="text-center">
                <Link
                  to="/login"
                  className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to Login
                </Link>
              </div>
            </form>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default ForgotPasswordPage; 